'use client'

import { cn } from '@brew/ui/cn'
import Image from 'next/image'
import type { BrandGuidePick } from '../../brand-guide/brand-guide-picks'

/**
 * The brands the guide can show, as pills on the page: each brand's mark
 * (on a round white chip, so every logo reads in both themes; an app-tile
 * mark fills the chip and is clipped to its circle) and name, the shown one
 * inverted. While the guide plays on its own, an invisible timer runs in
 * the shown pill and, once it ends, hands over to the next brand
 * (`onElapsed`, on its `animationend`): no interval runs. Hovering the
 * section holds the timer (`group/brand-guide` on its column), and under
 * reduced motion it never renders, so the guide never moves by itself.
 */
export function BrandGuidePicker({
  brands,
  activeKey,
  isPlaying,
  onSelect,
  onElapsed,
}: {
  brands: ReadonlyArray<BrandGuidePick>
  activeKey: string
  isPlaying: boolean
  onSelect: (key: string) => void
  onElapsed: () => void
}) {
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-2"
      data-brand-guide-picker=""
    >
      {brands.map((brand) => {
        const isActive = brand.key === activeKey
        return (
          <button
            key={brand.key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onSelect(brand.key)}
            className={cn(
              'relative flex h-10 cursor-pointer items-center gap-2 overflow-hidden rounded-full pr-4 pl-1.5 font-medium font-mk-body text-[14px] tracking-[-0.01em] transition-[background-color,color,box-shadow,scale] duration-200 ease-out focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2 active:scale-[0.97] motion-reduce:transition-none',
              isActive
                ? 'bg-mk-ink text-mk-card shadow-[0_8px_20px_-8px_rgb(30_30_30/0.45)]'
                : 'bg-mk-card text-mk-ink-2 shadow-[0_0_0_1px_var(--mk-card-line)] hover:text-mk-ink hover:shadow-[0_0_0_1px_var(--mk-ring)]'
            )}
          >
            <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white ring-1 ring-black/[0.06]">
              {brand.markFills ? (
                <Image
                  src={brand.markUrl}
                  alt=""
                  width={28}
                  height={28}
                  sizes="28px"
                  className="size-full object-cover"
                />
              ) : (
                <Image
                  src={brand.markUrl}
                  alt=""
                  width={20}
                  height={20}
                  sizes="20px"
                  className="size-5 object-contain"
                />
              )}
            </span>
            {brand.name}
            {isActive && isPlaying ? (
              <span
                aria-hidden="true"
                className="pointer-events-none invisible absolute inset-0 animate-mk-progress motion-reduce:hidden group-hover/brand-guide:[animation-play-state:paused]"
                onAnimationEnd={onElapsed}
              />
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
